/**
 * Centralized Bilingual Ad Translation & Display Helper
 * Cameroon Land Market (EN / FR)
 * 
 * Guarantees zero-empty-text fallback, accurate locale resolution,
 * entity protection, and user-facing "View original" toggle support.
 */

import type { Property } from '../types.ts';
import { translateFrToEn, translateEnToFr, detectLanguage, normalizeStylizedUnicode, cleanEnglishTranslation, cleanFrenchTranslation } from './translationService.ts';

export type LocaleLang = 'en' | 'fr';

export interface AdDisplayContent {
  title: string;
  description: string;
  originalLang: LocaleLang;
  currentLang: LocaleLang;
  isTranslated: boolean;
  canToggleOriginal: boolean;
  translationSource: 'auto' | 'manual';
  translationStatus: 'completed' | 'fallback' | 'failed' | 'none';
}

/**
 * Normalizes language string into strictly 'en' | 'fr'
 */
export function normalizeLocale(lang?: string | null): LocaleLang {
  if (!lang) return 'en';
  const clean = lang.toLowerCase().trim();
  if (clean.startsWith('fr')) return 'fr';
  return 'en';
}

/**
 * Resolves the appropriate title and description for an ad according to the current locale
 * and user's "View original" toggle state.
 */
export function getListingDisplayContent(
  property: Property | Record<string, any> | null | undefined,
  currentLangInput?: string | null,
  showOriginal: boolean = false
): AdDisplayContent {
  const currentLang = normalizeLocale(currentLangInput);

  if (!property) {
    return {
      title: '',
      description: '',
      originalLang: 'en',
      currentLang,
      isTranslated: false,
      canToggleOriginal: false,
      translationSource: 'auto',
      translationStatus: 'none',
    };
  }

  const rawTitle = normalizeStylizedUnicode((property.title || '').trim());
  const rawDescription = normalizeStylizedUnicode((property.description || '').trim());

  // 1. Determine original language
  const detectedOriginal: LocaleLang =
    (property.original_lang as LocaleLang) ||
    (property.originalLanguage as LocaleLang) ||
    (property.original_language as LocaleLang) ||
    detectLanguage(`${rawTitle} ${rawDescription}`);

  // 2. Extract localized fields with fallback cascade
  const titleEn = cleanEnglishTranslation((property.title_en || property.localizedTitle?.en || property.localized_title?.en || '').trim());
  const titleFr = cleanFrenchTranslation((property.title_fr || property.localizedTitle?.fr || property.localized_title?.fr || '').trim());

  const descEn = cleanEnglishTranslation((property.description_en || property.localizedDescription?.en || property.localized_description?.en || '').trim());
  const descFr = cleanFrenchTranslation((property.description_fr || property.localizedDescription?.fr || property.localized_description?.fr || '').trim());

  // Detect faulty previous translations where English was saved identical to French or still contains untranslated French phrases
  const hasUntranslatedFrenchInEnTitle = Boolean(
    titleEn &&
      detectedOriginal === 'fr' &&
      (titleEn.toLowerCase() === (titleFr || rawTitle).toLowerCase() ||
        /(?:\bterrain\s+titr[ée]\b|\b(?:[àa]|to)\s+vendre\b|\bloti\b|\bmorcelable\b)/i.test(titleEn))
  );
  const effectiveTitleEn = hasUntranslatedFrenchInEnTitle
    ? translateFrToEn(titleEn || titleFr || rawTitle)
    : (titleEn || (detectedOriginal === 'fr' ? translateFrToEn(rawTitle) : rawTitle));

  const isTitleFrIdenticalToEn = Boolean(
    titleFr &&
      detectedOriginal === 'en' &&
      (titleFr.toLowerCase() === (titleEn || rawTitle).toLowerCase() ||
        /(?:\bplots?\s+of\s+land\b|\bland\s+for\s+sale\b|\bfor\s+sale\b)/i.test(titleFr))
  );
  const effectiveTitleFr = isTitleFrIdenticalToEn
    ? translateEnToFr(titleFr || titleEn || rawTitle)
    : (titleFr || (detectedOriginal === 'en' ? translateEnToFr(rawTitle) : rawTitle));

  const hasUntranslatedFrenchInEnDesc = Boolean(
    descEn &&
      detectedOriginal === 'fr' &&
      (descEn.toLowerCase() === (descFr || rawDescription).toLowerCase() ||
        /(?:\bterrain\s+titr[ée]\b|\btitr[ée],\s*loti|\bpourquoi\s+choisir\s+ce\s+site\b|\ble\s+terrain\s+est\s+situ[ée]\b)/i.test(descEn))
  );
  const effectiveDescEn = hasUntranslatedFrenchInEnDesc
    ? translateFrToEn(descEn || descFr || rawDescription)
    : (descEn || (detectedOriginal === 'fr' ? translateFrToEn(rawDescription) : rawDescription));

  const isDescFrIdenticalToEn = Boolean(
    descFr &&
      detectedOriginal === 'en' &&
      descFr.toLowerCase() === (descEn || rawDescription).toLowerCase()
  );
  const effectiveDescFr = isDescFrIdenticalToEn
    ? translateEnToFr(descFr || descEn || rawDescription)
    : (descFr || (detectedOriginal === 'en' ? translateEnToFr(rawDescription) : rawDescription));

  const translationSource: 'auto' | 'manual' = property.translation_source === 'manual' ? 'manual' : 'auto';
  const translationStatus = property.translation_status || (titleEn && titleFr ? 'completed' : 'none');

  // If user requested to view original, or if current locale matches original language:
  if (showOriginal || currentLang === detectedOriginal) {
    const originalTitle = detectedOriginal === 'fr' 
      ? (titleFr || rawTitle || titleEn)
      : (titleEn || rawTitle || titleFr);

    const originalDesc = detectedOriginal === 'fr'
      ? (descFr || rawDescription || descEn)
      : (descEn || rawDescription || descFr);

    return {
      title: originalTitle || rawTitle || (currentLang === 'fr' ? 'Terrain / Propriété' : 'Real Estate Property'),
      description: originalDesc || rawDescription || '',
      originalLang: detectedOriginal,
      currentLang,
      isTranslated: false,
      canToggleOriginal: currentLang !== detectedOriginal && Boolean(titleEn || titleFr),
      translationSource,
      translationStatus,
    };
  }

  // 3. User is viewing in the translated language (e.g. English ad on /fr, or French ad on /en)
  let chosenTitle = '';
  let chosenDesc = '';
  let hasValidTranslation = false;

  if (currentLang === 'fr') {
    if (effectiveTitleFr) {
      chosenTitle = effectiveTitleFr;
      hasValidTranslation = true;
    } else {
      chosenTitle = rawTitle || effectiveTitleEn;
    }

    if (effectiveDescFr) {
      chosenDesc = effectiveDescFr;
    } else {
      chosenDesc = rawDescription || effectiveDescEn;
    }
  } else {
    // currentLang === 'en'
    if (effectiveTitleEn) {
      chosenTitle = effectiveTitleEn;
      hasValidTranslation = true;
    } else {
      chosenTitle = rawTitle || effectiveTitleFr;
    }

    if (effectiveDescEn) {
      chosenDesc = effectiveDescEn;
    } else {
      chosenDesc = rawDescription || effectiveDescFr;
    }
  }

  // Ensure never empty
  if (!chosenTitle) {
    chosenTitle = rawTitle || (currentLang === 'fr' ? 'Terrain / Propriété' : 'Real Estate Property');
  }

  return {
    title: chosenTitle,
    description: chosenDesc || rawDescription || '',
    originalLang: detectedOriginal,
    currentLang,
    isTranslated: hasValidTranslation && currentLang !== detectedOriginal,
    canToggleOriginal: Boolean(hasValidTranslation && currentLang !== detectedOriginal),
    translationSource,
    translationStatus,
  };
}

/**
 * Convenient shortcut for listing cards and directory listings
 */
export function getListingTitle(
  property: Property | Record<string, any> | null | undefined,
  currentLang?: string | null
): string {
  return getListingDisplayContent(property, currentLang, false).title;
}

/**
 * Convenient shortcut for listing cards and summaries
 */
export function getListingDescription(
  property: Property | Record<string, any> | null | undefined,
  currentLang?: string | null
): string {
  return getListingDisplayContent(property, currentLang, false).description;
}
